import React, { useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import { getHelp } from "../help/helpContent";
import { DIALOG_HEIGHT } from "./dialogSize";

/* Per-screen help.
 *
 * Two panes: section headers down the left, the selected section's detail on the
 * right. Each pane scrolls independently and the dialog itself never grows past
 * the viewport, so a long section cannot push the close button off screen.
 *
 * Content comes from src/help/helpContent.js — this component only renders it.
 * Adding help to a screen is therefore: write the content, then drop a
 * <HelpButton screen="…" /> into that screen's header.
 *
 * Type scale note: this is documentation, so it is set a step larger than the
 * app chrome around it. Reading a few paragraphs at the 11-12px used for dense
 * tables and badges is uncomfortable, and the footer controls need real hit
 * targets rather than the smallest thing that fits.
 */

/* ------------------------------------------------------------------ */
/*  Block renderers                                                    */
/* ------------------------------------------------------------------ */

function Block({ block }) {
  switch (block.type) {
    case "text":
      return <p className="text-[15px] leading-relaxed text-slate-700">{block.text}</p>;

    case "list":
      return (
        <ul className="space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-slate-700">
              <span className="mt-[9px] h-1.5 w-1.5 flex-none rounded-full bg-slate-400" />
              <span className="min-w-0">{item}</span>
            </li>
          ))}
        </ul>
      );

    case "steps":
      return (
        <ol className="space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
                {i + 1}
              </span>
              <span className="min-w-0 pt-0.5 text-[15px] leading-relaxed text-slate-700">
                {item}
              </span>
            </li>
          ))}
        </ol>
      );

    case "fields":
      return (
        <dl className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
          {block.items.map((f, i) => (
            <div key={i} className="grid grid-cols-1 gap-1 px-4 py-3.5 sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-semibold text-slate-900">{f.term}</dt>
              <dd className="text-[15px] leading-relaxed text-slate-700 sm:col-span-2">
                {f.desc}
              </dd>
            </div>
          ))}
        </dl>
      );

    case "note": {
      const warn = block.tone === "warn";
      return (
        <div
          className={[
            "flex gap-2.5 rounded-xl border px-4 py-3.5",
            warn ? "border-amber-200 bg-amber-50" : "border-sky-200 bg-sky-50",
          ].join(" ")}
        >
          <Icons.Alert
            className={["mt-0.5 h-5 w-5 flex-none", warn ? "text-amber-600" : "text-sky-600"].join(" ")}
          />
          <p
            className={[
              "text-[15px] leading-relaxed",
              warn ? "text-amber-900" : "text-sky-900",
            ].join(" ")}
          >
            {block.text}
          </p>
        </div>
      );
    }

    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Dialog                                                             */
/* ------------------------------------------------------------------ */

export function HelpDialog({ screen, onClose }) {
  const help = useMemo(() => getHelp(screen), [screen]);
  const sections = help?.sections || [];
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const detailRef = useRef(null);
  const closeRef = useRef(null);

  // Close on Escape, and move focus to the dialog so keyboard users land inside
  // it rather than continuing to tab through the page behind.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Jumping to a section should start that section at the top, not keep the
  // previous section's scroll offset.
  useEffect(() => {
    if (detailRef.current) detailRef.current.scrollTop = 0;
  }, [activeId]);

  if (!help) return null;

  const active = sections.find((s) => s.id === activeId) || sections[0];
  const activeIndex = sections.findIndex((s) => s.id === active?.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Help: ${help.title}`}
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-[90vw] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200`}
      >
        {/* Header */}
        <div className="flex flex-none items-start gap-3.5 border-b border-slate-200 px-6 py-5">
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <Icons.Help className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">
              {help.title}
            </h2>
            <p className="mt-1 text-[15px] leading-snug text-slate-500">{help.subtitle}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        {/* Two panes, each scrolling on its own. */}
        <div className="flex min-h-0 flex-1">
          {/* Left: section headers */}
          <nav
            aria-label="Help sections"
            className="cq-scroll w-72 flex-none overflow-y-auto border-r border-slate-200 bg-slate-50/60 p-3"
          >
            {sections.map((s) => {
              const isActive = s.id === active?.id;
              const Icon = Icons[s.icon] || Icons.ChevronRight;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveId(s.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={[
                    "mb-1 flex w-full items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition",
                    isActive
                      ? "bg-white font-semibold text-brand-800 shadow-sm ring-1 ring-brand-600/15"
                      : "text-slate-600 hover:bg-white/70 hover:text-slate-900",
                  ].join(" ")}
                >
                  <Icon
                    className={[
                      "mt-0.5 h-4 w-4 flex-none",
                      isActive ? "text-brand-600" : "text-slate-400",
                    ].join(" ")}
                  />
                  <span className="min-w-0 flex-1 text-[14.5px] leading-snug">{s.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Right: the selected section */}
          <div ref={detailRef} className="cq-scroll min-w-0 flex-1 overflow-y-auto px-8 py-7">
            {/* The dialog is 90vw, so this pane can be ~1100px wide. Prose at that
                measure is hard to read, so the reading column is capped. */}
            {active && (
              <div className="mx-auto max-w-3xl">
                <h3 className="text-2xl font-semibold tracking-tight text-slate-900">
                  {active.label}
                </h3>
                <div className="mt-5 space-y-5">
                  {(active.blocks || []).map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer: move between sections without going back to the left pane. */}
        <div className="flex flex-none items-center justify-between gap-4 border-t border-slate-200 bg-slate-50/60 px-6 py-3.5">
          <span className="text-sm text-slate-500">
            Section {activeIndex + 1} of {sections.length}
            {" · "}
            <span className="hidden sm:inline">Esc to close</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={activeIndex <= 0}
              onClick={() => setActiveId(sections[activeIndex - 1].id)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
            >
              <Icons.ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              type="button"
              disabled={activeIndex >= sections.length - 1}
              onClick={() => setActiveId(sections[activeIndex + 1].id)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
            >
              Next
              <Icons.ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Button                                                             */
/* ------------------------------------------------------------------ */

/** Help affordance for a screen header. Renders nothing when the screen has no
 *  content registered, so an un-documented screen shows no dead button. */
export default function HelpButton({ screen, className = "" }) {
  const [open, setOpen] = useState(false);
  const has = !!getHelp(screen);

  if (!has) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="What is this screen?"
        aria-label="What is this screen?"
        className={[
          "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-card text-slate-400 shadow-sm transition hover:border-brand-300 hover:text-brand-600",
          className,
        ].join(" ")}
      >
        <Icons.Help className="h-5 w-5" />
      </button>
      {open && <HelpDialog screen={screen} onClose={() => setOpen(false)} />}
    </>
  );
}
